import type { FunctionComponent, SVGProps } from 'react';
import type { CompaniesResponse } from '@/services/company-service/company-service.types';
import type { PlanTier, User, UserRole } from '@/services/user-service/user.service.types';
import DashboardIcon from '@/assets/icons/dots-grid.svg?react';
import ConversationsIcon from '@/assets/icons/message-chat-square.svg?react';
import StatisticsIcon from '@/assets/icons/bar-chart-01.svg?react';
import SatisfactionIcon from '@/assets/icons/face-smile.svg?react';
import KnowledgeBaseIcon from '@/assets/icons/book-closed.svg?react';
import SettingsIcon from '@/assets/icons/settings-01.svg?react';
import OutboundCallsIcon from '@/assets/icons/phone-outgoing-01.svg?react';

export const VISIBLE_SECTION_KEYS = [
  'dashboard',
  'conversations',
  'outbound-calls',
  'statistics',
  'satisfaction',
  'documents',
  'company',
] as const;

export type SectionKey = (typeof VISIBLE_SECTION_KEYS)[number];

export type NavGroup = 'main' | 'config';

/**
 * Mirrored onto the route guard in `routes.tsx`, so nav and routing cannot drift. Holds
 * every check `RoleBasedRoute` makes, and `canAccess` honours all of them; a gate added to
 * one side alone is what lets the menu offer a route the router then bounces.
 */
export type AccessGate = {
  allowedRoles?: UserRole[];
  deniedRoles?: UserRole[];
  allowedPlanTiers?: PlanTier[];
  requireConversationalAgent?: boolean;
  /** Read from `GET /companies`, not the user: one of the user's companies can call out. */
  requireOutboundCalls?: boolean;
};

export type Section = AccessGate & {
  /**
   * Also the AntD menu key, the first path segment and the key into the `navigation`
   * catalogue, where the section's label, title and subtitle live. The label is the
   * sidebar text and differs from the title where the mockup's own TITLES map differs.
   */
  key: SectionKey;
  path: string;
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  group: NavGroup;
  showRangeSwitch: boolean;
  demoBadge: boolean;
};

/** Hidden unconditionally, including for admins. The screens stay in the repo. */
export const HIDDEN_SECTION_KEYS = [
  'command_center',
  'surveys',
  'work-orders',
  'phone-numbers',
  'voices',
  'agents',
  'support',
] as const;

/**
 * Routed but absent from the menu: the sidebar chip is the only way in, so its gate has to
 * live where the route and the chip can both read it, or one of them starts offering what
 * the other refuses. Reaching it is what keeps the change-password form
 * (`PUT /auth/password`) available.
 */
export const PROFILE_SECTION: { key: 'profile'; path: string } & AccessGate = {
  key: 'profile',
  path: '/profile',
  deniedRoles: ['reviewer'],
};

export const UNLISTED_SECTION_KEYS = [PROFILE_SECTION.key] as const;

/** Just the gate fields, for spreading onto a route definition. */
export function accessGate(gate: AccessGate): AccessGate {
  return {
    allowedRoles: gate.allowedRoles,
    deniedRoles: gate.deniedRoles,
    allowedPlanTiers: gate.allowedPlanTiers,
    requireConversationalAgent: gate.requireConversationalAgent,
    requireOutboundCalls: gate.requireOutboundCalls,
  };
}

// Unhiding an area means dropping its breadcrumb too, or it renders two titles.

/**
 * `demoBadge` marks a section with no real data. It is independent of
 * `showRangeSwitch`: a screen can badge individual cards and still filter by range.
 * The mockup badges every section; ours all show at least one real card, so a
 * page-level badge would assert something false. Partly demo screens badge the card.
 */
export const VISIBLE_SECTIONS: Section[] = [
  {
    key: 'dashboard',
    path: '/',
    icon: DashboardIcon,
    group: 'main',
    showRangeSwitch: true,
    demoBadge: false,
  },
  {
    key: 'conversations',
    path: '/conversations',
    icon: ConversationsIcon,
    group: 'main',
    showRangeSwitch: true,
    demoBadge: false,
  },
  {
    key: 'outbound-calls',
    path: '/outbound-calls',
    icon: OutboundCallsIcon,
    group: 'main',
    showRangeSwitch: false,
    demoBadge: false,
    allowedRoles: ['admin', 'company_admin'],
    requireOutboundCalls: true,
  },
  {
    key: 'statistics',
    path: '/statistics',
    icon: StatisticsIcon,
    group: 'main',
    showRangeSwitch: true,
    demoBadge: false,
  },
  {
    key: 'satisfaction',
    path: '/satisfaction',
    icon: SatisfactionIcon,
    group: 'main',
    showRangeSwitch: true,
    // The rating card is real. Only the quotes card is a mock, and it carries its own badge.
    demoBadge: false,
  },
  {
    key: 'documents',
    path: '/documents',
    icon: KnowledgeBaseIcon,
    group: 'config',
    showRangeSwitch: false,
    demoBadge: false,
    allowedPlanTiers: ['Custom'],
  },
  {
    key: 'company',
    path: '/company',
    icon: SettingsIcon,
    group: 'config',
    showRangeSwitch: false,
    demoBadge: false,
    allowedRoles: ['admin', 'company_admin'],
  },
];

/** Matches on the first path segment, so detail routes resolve to their parent section. */
export function findSectionByPath(pathname: string): Section | undefined {
  const key = pathname === '/' ? 'dashboard' : pathname.split('/')[1];

  return VISIBLE_SECTIONS.find((section) => section.key === key);
}

/**
 * A destination the route guard would bounce must not be offered, so this mirrors every
 * check in `RoleBasedRoute`.
 */
export function canAccess(
  gate: AccessGate,
  userData?: User,
  companies?: CompaniesResponse
): boolean {
  const gated =
    gate.allowedRoles ||
    gate.deniedRoles ||
    gate.allowedPlanTiers ||
    gate.requireConversationalAgent ||
    gate.requireOutboundCalls;
  if (!gated) return true;
  if (!userData) return false;

  const roles = userData.roles?.map((role) => role.name) ?? [];

  if (gate.deniedRoles?.some((role) => roles.includes(role))) return false;

  if (gate.allowedRoles && !gate.allowedRoles.some((role) => roles.includes(role))) return false;

  if (gate.allowedPlanTiers && !gate.allowedPlanTiers.includes(userData.plan_tier)) return false;

  if (gate.requireConversationalAgent && userData.conversational_agent === false) return false;

  if (gate.requireOutboundCalls && !canCallOut(companies)) return false;

  return true;
}

/** Any of these companies can place an outbound call. */
export const canCallOut = (companies?: CompaniesResponse) =>
  !!companies?.some((company) => company.outbound_calls_enabled);
